import { inject, Injectable, Injector } from '@angular/core';
import Dexie, { Collection, Table } from 'dexie';
import { BehaviorSubject } from 'rxjs';
import { Utilities } from '../class/utilities';
import { Company } from '../interface/company';
import {
  EndedRoListSyncDelta,
  EndedRoListSyncRow,
  EndedRoLocalQuery,
  EndedRoLocalQueryResult,
  EndedRoLocalRecord,
  EndedRoSyncMetadata
} from '../interface/ended-ro-list-sync';
import { RoService } from './ro.service';
import { PaginatedOS } from '@app/interface/paginated-os';

const SCHEMA_VERSION = 4;
const METADATA_STORAGE_KEY = 'ro-ended-list-sync:last-company-id';
const SEARCH_STOP_WORDS = new Set([
  'a', 'as', 'o', 'os', 'um', 'uma', 'uns', 'umas',
  'ao', 'aos', 'de', 'da', 'das', 'do', 'dos',
  // 'em', 'no', 'na', 'nos', 'nas',
  // 'para', 'por', 'per', 'pel', 'pelo', 'pela', 'pelos', 'pelas',
  // 'com', 'sem', 'sobre', 'entre', 'até', 'ate', 'após', 'apos', 'antes', 'desde',
  // 'contra', 'durante', 'mediante', 'perante', 'sob', 'trás', 'tras'
]);

@Injectable({
  providedIn: 'root'
})
export class RoEndedIndexedDbService extends Dexie {
  private injector = inject(Injector);
  private _roService!: RoService;
  private _endedRosDb!: Table<EndedRoLocalRecord, string>;
  private _activeCompanyId?: string;
  private _syncQueue = Promise.resolve();

  syncMetadata$ = new BehaviorSubject<EndedRoSyncMetadata>(null);
  syncFinished$ = new BehaviorSubject<number>(0);

  constructor() {
    super('ro-ended-list');

    this.version(4).stores({
      endedRos: 'id, companyId, receiveDate, codeSystem, budgetCode, type, *searchTokens'
    });

    this._endedRosDb = this.table<EndedRoLocalRecord, string>('endedRos');
  }

  private get roService(): RoService {
    if (!this._roService) {
      this._roService = this.injector.get(RoService);
    }
    return this._roService;
  }

  async syncCompany(company: Company): Promise<void> {
    await this.syncCompanyInternal(company);
  }

  async clearCurrentCompany(companyId: string): Promise<void> {
    await this._endedRosDb.clear();
    const metadata = this.defaultMetadata(companyId);
    this.saveMetadata(metadata);
    this.syncMetadata$.next(metadata);
  }

  reconcileAfterMutation(roId?: string): void {
    if (!roId) {
      return;
    }

    const companyId = this._activeCompanyId || this.syncMetadata$.value?.companyId;
    if (!companyId) {
      return;
    }

    void this.reconcileAfterMutationInternal(companyId, roId)
      .catch(error => console.error('[Ended RO IndexedDB] reconciliation failed', { companyId, roId }, error));
  }

  getMetadata(companyId: string): EndedRoSyncMetadata {
    const metadata = this.syncMetadata$.value;
    return metadata?.companyId === companyId
      ? metadata
      : this.defaultMetadata(companyId);
  }

  async query(query: EndedRoLocalQuery): Promise<EndedRoLocalQueryResult> {
    const page = Math.max(1, query.page || 1),
          limit = Math.max(1, query.limit || 10),
          search = this.normalizeSearchText(query.search || ''),
          numericSearch = search ? Number(search) : NaN,
          isNumericSearch = search && Number.isFinite(numericSearch),
          start = (page - 1) * limit,
          searchTokens = this.splitSearchText(search);

    const sort: Record<string, 1 | -1> = query.sort && Object.keys(query.sort).length ? query.sort : { receiveDate: -1 };
    const [[sortField, sortDirection]] = Object.entries(sort);
    const sortIndex = this.getSortIndex(sortField);

    // Filtered branches (codeSystem / searchTokens) yield collections ordered by their
    // `where` index, not by the requested sort. Sort in memory so paging respects `sort`.
    if (isNumericSearch || searchTokens.length) {
      const collection = isNumericSearch
        ? this.applyQueryFilters(this._endedRosDb.where('codeSystem').equals(numericSearch), query)
        : this.applyQueryFilters(this.createSearchCollection(searchTokens), query)
            .distinct()
            .filter((record) =>
              searchTokens.every((searchToken) =>
                record.searchTokens.some((recordToken) =>
                  recordToken.startsWith(searchToken)
                )
              )
            );

      const matches = (await collection.toArray())
        .sort((a, b) => this.compareRecords(a, b, sortIndex, sortDirection));

      return {
        docs: matches.slice(start, start + limit),
        totalDocs: matches.length,
        page,
        limit
      };
    }

    const collectionFactory = () => this.applyQueryFilters(this.createBaseCollection(sort), query);

    return {
      docs: await collectionFactory().offset(start).limit(limit).toArray(),
      totalDocs: await collectionFactory().count(),
      page,
      limit
    };
  }

  private compareRecords(
    a: EndedRoLocalRecord,
    b: EndedRoLocalRecord,
    field: string,
    direction: number
  ): number {
    const av = a[field as keyof EndedRoLocalRecord];
    const bv = b[field as keyof EndedRoLocalRecord];

    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return direction === -1 ? 1 : -1;
    if (av > bv) return direction === -1 ? -1 : 1;
    return 0;
  }

  private async syncCompanyInternal(company: Company): Promise<void> {
    const companyId = company?.id;
    if (!companyId) {
      return;
    }

    const changedCompany = this.getStoredCompanyId() !== companyId;
    this._activeCompanyId = companyId;
    let metadata = this.getMetadata(companyId);

    if (changedCompany || metadata.schemaVersion !== SCHEMA_VERSION) {
      await this.clearCurrentCompany(companyId);
      metadata = this.getMetadata(companyId);
    }

    await this.setInProgress(metadata, true);

    try {
      if (!metadata.populated || !metadata.lastServerUpdate) {
        const delta = await this.roService.getEndedListSyncDelta();
        await this.applyDelta(companyId, delta, true);
        return;
      }

      const status = await this.roService.getEndedListSyncStatus(metadata.lastServerUpdate);
      if (!status.changed) {
        await this.saveCompletedMetadata(companyId, status.latestUpdatedAt || metadata.lastServerUpdate, true);
        return;
      }

      const delta = await this.roService.getEndedListSyncDelta(metadata.lastServerUpdate);
      await this.applyDelta(companyId, delta, !!delta.fullReloadRequired);
    } finally {
      const latest = this.getMetadata(companyId);
      if (latest.inProgress) {
        latest.inProgress = false;
        this.saveMetadata(latest);
        this.syncMetadata$.next(latest);
      }
    }
  }

  private async applyDelta(companyId: string, delta: EndedRoListSyncDelta, clearBeforeApply: boolean): Promise<void> {
    const upserts = (delta.upserts || [])
      .map(row => this.normalizeForDb(row))
      .filter(row => row.companyId === companyId);
    const removalIds = (delta.removals || [])
      .filter(removal => !removal.companyId || removal.companyId === companyId)
      .map(removal => removal.id)
      .filter((id): id is string => !!id);

    await this.transaction('rw', this._endedRosDb, async () => {
      if (clearBeforeApply) {
        await this._endedRosDb.clear();
      }

      if (upserts.length) {
        await this._endedRosDb.bulkPut(upserts);
      }

      if (removalIds.length) {
        await this._endedRosDb.bulkDelete(removalIds);
      }
    });

    await this.saveCompletedMetadata(companyId, delta.latestUpdatedAt, true);
  }

  private async reconcileAfterMutationInternal(companyId: string, roId: string): Promise<void> {
    const result = await this.roService.getEndedListSyncOne(roId);
    const metadata = this.getMetadata(companyId);

    if (this._activeCompanyId && this._activeCompanyId !== companyId) {
      return;
    }

    const row = result.result === 'upsert' && result.row ? this.normalizeForDb(result.row) : null;

    if (row?.companyId === companyId) {
      await this._endedRosDb.put(row);
    } else {
      await this._endedRosDb.delete(roId);
    }

    if (result.latestUpdatedAt) {
      await this.saveCompletedMetadata(companyId, result.latestUpdatedAt, metadata.populated);
    } else {
      this.syncFinished$.next(Date.now());
    }
  }

  private normalizeForDb(row: EndedRoListSyncRow): EndedRoLocalRecord {
    const rowSearchTokens = row.searchTokens || row.searchToken;
    const searchTokens = rowSearchTokens?.length
      ? this.buildSearchTokens(...rowSearchTokens)
      : this.buildSearchTokens(row.vehicleModel, row.vehiclePlate, row.clientName);
    const { searchToken: _searchToken, ...rowWithoutLegacySearchToken } = row;

    return {
      ...rowWithoutLegacySearchToken,
      id: this.normalizeMongoId(row.id),
      companyId: this.normalizeMongoId(row.companyId),
      type: this.normalizeMongoId(row.type),
      user: this.normalizeMongoId(row.user),
      clientId: this.normalizeMongoId(row.clientId),
      vehicleId: this.normalizeMongoId(row.vehicleId),
      searchTokens
    };
  }

  private normalizeMongoId(value: any): any {
    if (!value || typeof value === 'string') return value;
    if (typeof value.toHexString === 'function') return value.toHexString();
    const bytes = value.id;
    if (bytes && typeof bytes.length === 'number') {
      return Array.from(bytes as ArrayLike<number>)
        .map(byte => Number(byte).toString(16).padStart(2, '0'))
        .join('');
    }
    return value;
  }

  private normalizeSearchText(text: string | number): string {
    const normalized = Utilities.removeDiacritics(String(text || '')).toLowerCase().replace(/[.\-/]/g, '').trim();
    return normalized.replace(/\s+/g, ' ');
  }

  private tokenizeSearchText(text: string | number): string[] {
    return this.normalizeSearchText(text)
      .split(' ')
      .filter(token => token && !SEARCH_STOP_WORDS.has(token));
  }

  private buildSearchTokens(...values: Array<string | number>): string[] {
    const tokens = new Set<string>();

    values.forEach(value => {
      const normalizedTokens = this.tokenizeSearchText(value);
      if (!normalizedTokens.length) {
        return;
      }

      normalizedTokens.forEach(token => tokens.add(token));

      if (normalizedTokens.length > 1) {
        tokens.add(normalizedTokens.join(' '));
      }
    });

    return Array.from(tokens);
  }

  private splitSearchText(search: string): string[] {
    return this.tokenizeSearchText(search);
  }

  private createSearchCollection(searchTokens: string[]): Collection<EndedRoLocalRecord, string> {
    return this._endedRosDb.where('searchTokens').startsWithAnyOf(searchTokens);
  }

  private applyQueryFilters(
    collection: Collection<EndedRoLocalRecord, string>,
    query: EndedRoLocalQuery
  ): Collection<EndedRoLocalRecord, string> {
    return collection.and(row =>
      row.companyId === query.companyId
      && (!query.user || row.user === query.user)
      && (!query.roType || row.type === query.roType)
    );
  }

  private createBaseCollection(sort: Record<string, 1 | -1>): Collection<EndedRoLocalRecord, string> {
    const [[field, direction]] = Object.entries(sort);
    const index = this.getSortIndex(field);
    const collection = this._endedRosDb.orderBy(index);

    console.log({index, collection, field, direction})
    return direction === -1 ? collection.reverse() : collection;
  }

  private getSortIndex(field: string): string {
    const aliases: Record<string, string> = {
      code: 'codeSystem',
      client: 'receiveDate',
      plate: 'receiveDate',
      model: 'receiveDate',
      createdAt: 'receiveDate',
      liquidValue: 'receiveDate'
    };
    const sortableField = aliases[field] || field;
    const sortableIndexes = new Set([
      'receiveDate',
      'codeSystem',
      'budgetCode',
      'type'
    ]);

    return sortableIndexes.has(sortableField) ? sortableField : 'receiveDate';
  }

  private async setInProgress(metadata: EndedRoSyncMetadata, inProgress: boolean): Promise<void> {
    const next = { ...metadata, inProgress };
    this.saveMetadata(next);
    this.syncMetadata$.next(next);
  }

  private async saveCompletedMetadata(companyId: string, latestUpdatedAt: string, populated: boolean): Promise<void> {
    const metadata: EndedRoSyncMetadata = {
      ...this.getMetadata(companyId),
      companyId,
      lastSyncAt: new Date().toISOString(),
      lastServerUpdate: latestUpdatedAt || this.getMetadata(companyId).lastServerUpdate,
      populated,
      inProgress: false,
      schemaVersion: SCHEMA_VERSION
    };

    this.saveMetadata(metadata);
    this.syncMetadata$.next(metadata);
    this.syncFinished$.next(Date.now());
  }

  private defaultMetadata(companyId: string): EndedRoSyncMetadata {
    return {
      companyId,
      populated: false,
      inProgress: false,
      schemaVersion: SCHEMA_VERSION
    };
  }

  private saveMetadata(metadata: EndedRoSyncMetadata): void {
    localStorage.setItem(METADATA_STORAGE_KEY, metadata.companyId);
  }

  private getStoredCompanyId(): string | null {
    return localStorage.getItem(METADATA_STORAGE_KEY);
  }

  toPaginatedOs(row: EndedRoLocalRecord): any {
    return {
      ...row,
      client: {
        id: row.clientId,
        name: row.clientName,
        address: null
      },
      clientTmp: row.clientName,
      vehicle: {
        id: row.vehicleId,
        plate: row.vehiclePlate,
        brand: '',
        model: row.vehicleModel
      },
      vehicleTmp: row.vehiclePlate,
      parts: [],
      labors: [],
      checklist: [],
      notes: [],
      chat: [],
      tags: [],
      paymentConditions: [],
      thirdPartyServices: [],
      breakdowns: null,
      liquidValue: (row.liquidValue || 0)
    };
  }
}
